import { useState } from "react";
import { C } from "./re-colors.ts";
import {
  AI_INSIGHTS,
  BOOK_OF_BUSINESS,
  BRIEFINGS,
  DEPOSIT_PROFITABILITY,
  EXAM_CATEGORIES,
  FAIR_LENDING_DATA,
  HMDA_LAR,
  MODEL_GOVERNANCE,
  PERSONAS,
  VENDOR_REGISTRY,
  WATCHLIST_CREDITS,
  _tasks,
} from "./re-data.ts";
import { CONFIGURABLE_PACKAGES, SOR_MOCK_CALLS } from "./re-packages.ts";
import { AiBox, Bar, Bdg, Card, InsightCard, KpiCard, PH, SDot, useIsMobile } from "./re-components.tsx";

type StoryMode = {
  id: string;
  label: string;
  headline: string;
  sub: string;
  defaultPersona: string;
  packageId: string;
  proof: string[];
};

const STORY_MODES: StoryMode[] = [
  {
    id: "growth",
    label: "Growth Bank",
    headline: "RegionalEdge gives expansion-minded institutions a single view of market growth, banker production, risk drift, and board-ready evidence.",
    sub: "The platform is designed for regional banks opening new markets without losing local decision speed or operating discipline.",
    defaultPersona: "ceo",
    packageId: "market_expansion",
    proof: ["Market-entry scorecards", "Relationship-manager league tables", "Board-ready growth narrative"],
  },
  {
    id: "sba",
    label: "SBA / CDFI",
    headline: "RegionalEdge connects origination, servicing, CRA strategy, and community impact in one operating layer.",
    sub: "The platform is a strong fit for banks with SBA, CDFI, CRA strategic-plan, or lender-service-provider complexity.",
    defaultPersona: "lending",
    packageId: "sba_servicing",
    proof: ["Servicing-asset visibility", "CRA/CDFI impact tracking", "Faster banker-to-operations handoff"],
  },
  {
    id: "ag",
    label: "Ag / Risk",
    headline: "RegionalEdge connects credit, guarantees, liquidity, and treasury exposure before problems reach committee.",
    sub: "The platform is built to surface agricultural concentration, seasonal liquidity, borrower stress, and government-guarantee workflows in one place.",
    defaultPersona: "credit",
    packageId: "ag_guarantees",
    proof: ["Guarantee eligibility workflow", "Insurance and collateral tracking", "Early warning before downgrade"],
  },
];

const PERSONA_METRICS: Record<string, { label: string; value: string; sub: string; trend?: "up" | "dn"; spark: number[]; color: string }[]> = {
  ceo: [
    { label: "Deposits", value: "$3.62B", sub: "34.1% uninsured", trend: "dn", spark: [3.42, 3.48, 3.52, 3.56, 3.6, 3.62], color: C.burgundy },
    { label: "SBA Pipeline", value: "$42M", sub: "7 pre-qualified", trend: "up", spark: [21, 24, 28, 31, 37, 42], color: C.gold },
    { label: "Exam Readiness", value: "84 / 100", sub: "2 domains at risk", trend: "dn", spark: [89, 88, 88, 86, 85, 84], color: C.forest },
  ],
  banker: [
    { label: "At-Risk Revenue", value: "$218K", sub: "5 relationships", trend: "dn", spark: [260, 252, 245, 238, 228, 218], color: C.burgundy },
    { label: "Products / HH", value: "3.8", sub: "target 4.1", trend: "up", spark: [3.1, 3.2, 3.3, 3.5, 3.6, 3.8], color: C.forest },
    { label: "Prospect Value", value: "$1.63M", sub: "SBA + TM opportunities", trend: "up", spark: [0.6, 0.72, 0.92, 1.1, 1.34, 1.63], color: C.gold },
  ],
  lending: [
    { label: "Loan Pipeline", value: "$14.1M", sub: "8 opportunities", trend: "up", spark: [8.9, 9.8, 10.4, 11.2, 12.9, 14.1], color: C.burgundy },
    { label: "Pull-Through", value: "76%", sub: "above 71% plan", trend: "up", spark: [61, 64, 66, 69, 73, 76], color: C.forest },
    { label: "Pricing Spread", value: "+41 bps", sub: "vs floor", trend: "up", spark: [18, 24, 29, 33, 38, 41], color: C.gold },
  ],
  credit: [
    { label: "Criticized Assets", value: "$47.3M", sub: "2 immediate actions", trend: "dn", spark: [41, 42, 43, 45, 46, 47.3], color: C.burgundy },
    { label: "ACL / Loans", value: "1.42%", sub: "+4 bps QoQ", trend: "up", spark: [1.31, 1.33, 1.35, 1.37, 1.39, 1.42], color: C.gold },
    { label: "Ag Insurance", value: "9 / 14", sub: "5 unverified", trend: "dn", spark: [14, 14, 13, 11, 10, 9], color: C.forest },
  ],
  compliance: [
    { label: "Open SARs", value: "4", sub: "1 due today", trend: "dn", spark: [7, 7, 6, 5, 5, 4], color: C.burgundy },
    { label: "HMDA Errors", value: "6", sub: "before submission", trend: "dn", spark: [12, 10, 9, 8, 7, 6], color: C.gold },
    { label: "CRA Rating", value: "Outstanding", sub: "exam Q4 2026", trend: "up", spark: [76, 81, 84, 87, 89, 92], color: C.forest },
  ],
  cro: [
    { label: "LCR", value: "182%", sub: "88% stressed", trend: "dn", spark: [205, 201, 197, 191, 186, 182], color: C.burgundy },
    { label: "EVE +200", value: "+$12.4M", sub: "within policy", trend: "up", spark: [8.1, 8.9, 9.7, 10.4, 11.6, 12.4], color: C.forest },
    { label: "AFS Duration", value: "5.8 yrs", sub: "limit 6.0", trend: "dn", spark: [4.8, 5.0, 5.2, 5.4, 5.6, 5.8], color: C.gold },
  ],
};

function ScreenFrame({ accent, title, sub, children }: { accent: string; title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 18px 50px rgba(42,31,24,.08)" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, background: `linear-gradient(90deg, ${accent}10, rgba(255,255,255,.92))`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[C.burgundy, C.gold, C.forest].map((c, i) => <span key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c, opacity: .65 }} />)}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{title}</div>
          {sub && <div style={{ fontSize: 10, color: C.muted }}>{sub}</div>}
        </div>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}

function CodeBlock({ label, text, dark = true }: { label: string; text: string; dark?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .7, marginBottom: 5 }}>{label}</div>
      <pre style={{ margin: 0, padding: 14, borderRadius: 12, background: dark ? "#131924" : "#F5F0EA", color: dark ? "#E7EDF8" : C.text, fontSize: 10, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word", overflowX: "auto", border: dark ? "1px solid rgba(255,255,255,.06)" : `1px solid ${C.border}` }}>
        {text}
      </pre>
    </div>
  );
}

function StoryStepButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ background: active ? C.navy : C.card, color: active ? "#fff" : C.muted, border: `1px solid ${active ? C.navy : C.border}`, borderRadius: 999, padding: "8px 14px", cursor: "pointer", fontWeight: 700, fontSize: 11 }}>
      {label}
    </button>
  );
}

export function ClientStoryPage({ onClose }: { onClose: () => void }) {
  const isMobile = useIsMobile();
  const [modeId, setModeId] = useState("growth");
  const [personaId, setPersonaId] = useState("ceo");
  const [flowStep, setFlowStep] = useState<"signal" | "workflow" | "proof">("signal");
  const [packageId, setPackageId] = useState("market_expansion");

  const mode = STORY_MODES.find(s => s.id === modeId) || STORY_MODES[0];
  const persona = PERSONAS.find(p => p.id === personaId) || PERSONAS[0];
  const metrics = PERSONA_METRICS[personaId] || PERSONA_METRICS.ceo;
  const briefing = BRIEFINGS[personaId] || BRIEFINGS.ceo;
  const insightBlocks = AI_INSIGHTS[personaId] || AI_INSIGHTS.ceo;
  const selectedPackage = CONFIGURABLE_PACKAGES.find(pkg => pkg.id === packageId) || CONFIGURABLE_PACKAGES[0];
  const relatedCalls = SOR_MOCK_CALLS.filter(call => call.packageIds.includes(selectedPackage.id));
  const creditCards = WATCHLIST_CREDITS.slice(0, 3);
  const fairnessRows = FAIR_LENDING_DATA.denialRates.slice(0, 4);
  const hmdaRows = HMDA_LAR.slice(0, 4);
  const governanceRows = MODEL_GOVERNANCE.slice(0, 4);
  const vendorRows = VENDOR_REGISTRY.slice(0, 4);
  const relationshipRows = BOOK_OF_BUSINESS.slice(0, 3);
  const taskRows = (_tasks[personaId] || _tasks.ceo).slice(0, 4);

  function switchMode(nextMode: StoryMode) {
    setModeId(nextMode.id);
    setPersonaId(nextMode.defaultPersona);
    setPackageId(nextMode.packageId);
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 650, background: "linear-gradient(180deg, #F4EEE7 0%, #FAF7F4 20%, #FAF7F4 100%)", overflowY: "auto" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 20, backdropFilter: "blur(12px)", background: "rgba(250,247,244,.86)", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1420, margin: "0 auto", padding: isMobile ? "12px 14px" : "14px 24px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 12, flexShrink: 0 }}>RE</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: C.text }}>RegionalEdge Platform Story</div>
            <div style={{ fontSize: 11, color: C.muted }}>A single-page product narrative built around working banking workflows</div>
          </div>
          <button onClick={onClose} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontWeight: 700, color: C.text, fontSize: 12 }}>
            Close
          </button>
        </div>
      </div>
      <div style={{ maxWidth: 1420, margin: "0 auto", padding: isMobile ? "18px 14px 28px" : "28px 24px 40px", display: "flex", flexDirection: "column", gap: 28 }}>
        <section style={{ position: "relative", overflow: "hidden", borderRadius: 28, background: "linear-gradient(135deg, #113158 0%, #2B5575 48%, #7B1C2E 100%)", color: "#fff", padding: isMobile ? "26px 18px" : "34px 34px" }}>
          <div style={{ position: "absolute", top: -60, right: -40, width: 260, height: 260, borderRadius: "50%", background: "rgba(255,255,255,.05)" }} />
          <div style={{ position: "absolute", bottom: -70, left: 80, width: 220, height: 220, borderRadius: "50%", background: "rgba(240,200,66,.07)" }} />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.15fr .95fr", gap: 22, position: "relative" }}>
            <div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <Bdg label="Single-Page Demo" color="#F0C842" />
                <Bdg label="Working Components" color="#D8E8FF" />
                <Bdg label="Platform Narrative" color="#BDE3CC" />
              </div>
              <h1 style={{ margin: "0 0 12px", fontSize: isMobile ? 28 : 42, lineHeight: 1.08, letterSpacing: -1.3, maxWidth: 780 }}>
                A single intelligence layer for growth, risk, compliance, and execution.
              </h1>
              <p style={{ margin: "0 0 18px", fontSize: 15, lineHeight: 1.7, color: "rgba(255,255,255,.82)", maxWidth: 760 }}>
                {mode.headline} {mode.sub}
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                {STORY_MODES.map(story => (
                  <button
                    key={story.id}
                    onClick={() => switchMode(story)}
                    style={{ background: mode.id === story.id ? "rgba(255,255,255,.18)" : "rgba(255,255,255,.08)", color: "#fff", border: `1px solid ${mode.id === story.id ? "rgba(255,255,255,.35)" : "rgba(255,255,255,.16)"}`, borderRadius: 999, padding: "9px 14px", cursor: "pointer", fontWeight: 700, fontSize: 11 }}
                  >
                    {story.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 10 }}>
                {mode.proof.map((item, i) => (
                  <Card key={i} style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)", color: "#fff", padding: 14 }}>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)", textTransform: "uppercase", letterSpacing: .7, marginBottom: 6 }}>Outcome {i + 1}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.45 }}>{item}</div>
                  </Card>
                ))}
              </div>
            </div>

            <ScreenFrame accent={C.gold} title="Live Morning Briefing" sub={`${persona.label} lens · AI + governed data`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{briefing.greeting}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{briefing.summary}</div>
                </div>
                <Bdg label="AI Live" color={C.aiPurple} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {briefing.items.map((item, i) => (
                  <Card key={i} style={{ padding: "12px 14px", background: C.surface }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <SDot s={item.urgency} />
                          <span style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .7 }}>{item.urgency}</span>
                        </div>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{item.text}</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </ScreenFrame>
          </div>
        </section>

        <section>
          <PH title="Act 1 — Every Persona Sees The Same Bank, Differently" sub="Switch the point of view and the page re-centers itself around that officer’s real job." accentColor={C.navy} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {["ceo", "banker", "lending", "credit", "compliance", "cro"].map(id => {
              const p = PERSONAS.find(x => x.id === id)!;
              const active = personaId === id;
              return (
                <button
                  key={id}
                  onClick={() => setPersonaId(id)}
                  style={{ background: active ? C.burgundy : C.card, color: active ? "#fff" : C.muted, border: `1px solid ${active ? C.burgundy : C.border}`, borderRadius: 999, padding: "8px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr .8fr", gap: 18 }}>
            <ScreenFrame accent={C.burgundy} title={`${persona.label} workspace`} sub="Working UI components powered by real product data">
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
                {metrics.map((m, i) => (
                  <KpiCard key={i} label={m.label} value={m.value} sub={m.sub} trend={m.trend} spark={m.spark} color={m.color} />
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                {insightBlocks.slice(0, 2).map((block, i) => (
                  <Card key={i} style={{ padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 16 }}>{block.icon}</span>
                      <div style={{ fontSize: 12, fontWeight: 800, color: block.color }}>{block.category}</div>
                    </div>
                    {block.insights.slice(0, 1).map((insight, idx) => (
                      <AiBox key={idx} text={`${insight.title} ${insight.body}`} c={block.color} source={insight.src} />
                    ))}
                  </Card>
                ))}
              </div>
            </ScreenFrame>

            <ScreenFrame accent={C.forest} title="Relationship & action layer" sub="This layer turns insight into banker action, credit visibility, and executive alignment.">
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                {relationshipRows.map((rel, i) => (
                  <InsightCard key={i} icon={i === 0 ? "🏢" : i === 1 ? "⚠️" : "🤝"} title={rel.name} body={`${rel.action}. Next best action: ${rel.nba}.`} bdg={rel.risk === "high" ? "At Risk" : rel.status} bColor={rel.risk === "high" ? C.burgundy : C.forest} priority={rel.risk === "high" ? "high" : "low"} />
                ))}
              </div>
              <AiBox text="RegionalEdge is not another dashboard. It is a single operating layer that connects banker action, credit quality, and executive visibility in one motion." c={C.navy} source="RegionalEdge platform narrative" />
            </ScreenFrame>
          </div>
        </section>

        <section>
          <PH title="Act 2 — Signal, Workflow, Proof" sub="The story works best when it moves from what the bank needs to know, to what the bank needs to do, to what the bank can prove." accentColor={C.gold} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            <StoryStepButton active={flowStep === "signal"} label="1. Signal" onClick={() => setFlowStep("signal")} />
            <StoryStepButton active={flowStep === "workflow"} label="2. Workflow" onClick={() => setFlowStep("workflow")} />
            <StoryStepButton active={flowStep === "proof"} label="3. Proof" onClick={() => setFlowStep("proof")} />
          </div>

          {flowStep === "signal" && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18 }}>
              <ScreenFrame accent={C.burgundy} title="Credit signal screen" sub="Early warning before committee">
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {creditCards.map((credit, i) => (
                    <Card key={i} style={{ padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 5 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{credit.name}</div>
                        <Bdg label={credit.classification} color={credit.classification === "Doubtful" ? C.burgundy : C.gold} />
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 8 }}>{credit.issue}</div>
                      <Bar val={credit.prob * 100} color={credit.prob >= 0.8 ? C.burgundy : C.gold} />
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Migration probability: {(credit.prob * 100).toFixed(0)}%</div>
                    </Card>
                  ))}
                </div>
              </ScreenFrame>

              <ScreenFrame accent={C.gold} title="Liquidity and fairness side effects" sub="The platform connects adjacent risks instead of leaving them in silos">
                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 10 }}>Deposit profitability snapshot</div>
                  {DEPOSIT_PROFITABILITY.segments.slice(0, 4).map((seg, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1.3fr .6fr .5fr", gap: 10, padding: "9px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ fontSize: 12, color: C.text }}>{seg.segment}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{seg.balanceFmt}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: seg.annualProfit > 0 ? C.forest : C.burgundy }}>{seg.rating}</div>
                    </div>
                  ))}
                </Card>
                <Card>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 10 }}>Fair lending pulse</div>
                  {fairnessRows.map((row, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: i < fairnessRows.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: C.text }}>{row.group}</span>
                        <span style={{ fontSize: 11, color: row.flag ? C.burgundy : C.forest, fontWeight: 700 }}>{row.bank}%</span>
                      </div>
                      <Bar val={row.bank} max={100} color={row.flag ? C.burgundy : C.forest} />
                    </div>
                  ))}
                </Card>
              </ScreenFrame>
            </div>
          )}

          {flowStep === "workflow" && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18 }}>
              <ScreenFrame accent={C.navy} title="Action queue" sub="Work gets assigned, escalated, and completed in context">
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {taskRows.map((task, i) => (
                    <Card key={i} style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 5 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{task.title}</div>
                        <Bdg label={task.priority} color={task.priority === "critical" ? C.burgundy : task.priority === "high" ? C.gold : C.forest} />
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>Due {task.due} · {task.category}</div>
                    </Card>
                  ))}
                </div>
                <AiBox text="RegionalEdge makes the platform feel operational rather than analytical: signals become owned actions with deadlines, recipients, and proof of follow-through." c={C.navy} source="RegionalEdge workflow narrative" />
              </ScreenFrame>

              <ScreenFrame accent={C.burgundy} title="Regulated workflow snippets" sub="The bank can move from issue to evidence without leaving the system">
                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>HMDA correction queue</div>
                  {hmdaRows.map((row, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr .6fr .6fr", gap: 8, padding: "8px 0", borderBottom: i < hmdaRows.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ fontSize: 12, color: C.text }}>{row.id}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{row.action}</div>
                      <div style={{ fontSize: 11, color: row.error ? C.burgundy : C.forest, fontWeight: 700 }}>{row.error ? "Needs fix" : "Clear"}</div>
                    </div>
                  ))}
                </Card>
                <Card>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>Escalation narrative</div>
                  <CodeBlock label="Mock Escalation Event" text={`POST /api/workflows/escalations\n{\n  "from": "${persona.label}",\n  "to": "Compliance",\n  "reason": "HMDA exceptions block quarter-end filing",\n  "priority": "high",\n  "delivery": ["in_app", "email", "teams"]\n}`} />
                </Card>
              </ScreenFrame>
            </div>
          )}

          {flowStep === "proof" && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18 }}>
              <ScreenFrame accent={C.forest} title="Board and exam proof" sub="Leadership sees readiness, not just raw data">
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {EXAM_CATEGORIES.slice(0, 5).map((cat, i) => (
                    <Card key={i} style={{ padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{cat.cat}</span>
                        <span style={{ fontSize: 11, color: cat.score < 80 ? C.burgundy : C.forest, fontWeight: 800 }}>{cat.score}</span>
                      </div>
                      <Bar val={cat.score} color={cat.score < 80 ? C.burgundy : cat.score < 90 ? C.gold : C.forest} />
                    </Card>
                  ))}
                </div>
              </ScreenFrame>

              <ScreenFrame accent={C.gold} title="Governance and vendor resilience" sub="Model controls and third-party risk on the same page">
                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>Model governance</div>
                  {governanceRows.map((row, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: i < governanceRows.length - 1 ? `1px solid ${C.border}` : "none", display: "grid", gridTemplateColumns: "1fr .65fr .5fr", gap: 8 }}>
                      <div style={{ fontSize: 12, color: C.text }}>{row.name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{row.owner}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: row.status === "Overdue" ? C.burgundy : row.status === "Due Soon" ? C.gold : C.forest }}>{row.status}</div>
                    </div>
                  ))}
                </Card>
                <Card>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>Vendor risk</div>
                  {vendorRows.map((row, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: i < vendorRows.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: C.text }}>{row.vendor}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: row.riskScore >= 80 ? C.burgundy : row.riskScore >= 65 ? C.gold : C.forest }}>{row.riskScore}</span>
                      </div>
                      <Bar val={row.riskScore} color={row.riskScore >= 80 ? C.burgundy : row.riskScore >= 65 ? C.gold : C.forest} />
                    </div>
                  ))}
                </Card>
              </ScreenFrame>
            </div>
          )}
        </section>

        <section>
          <PH title="Act 3 — Specialty Packages Prove Modular Fit" sub="Specialty packages extend the core platform without fragmenting the operating model." accentColor={C.aiPurple} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {CONFIGURABLE_PACKAGES.map(pkg => {
              const active = packageId === pkg.id;
              return (
                <button key={pkg.id} onClick={() => setPackageId(pkg.id)} style={{ background: active ? C.aiPurple : C.card, color: active ? "#fff" : C.muted, border: `1px solid ${active ? C.aiPurple : C.border}`, borderRadius: 999, padding: "8px 13px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                  {pkg.title}
                </button>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : ".8fr 1.2fr", gap: 18 }}>
            <ScreenFrame accent={C.aiPurple} title={selectedPackage.title} sub={selectedPackage.strapline}>
              <Card style={{ marginBottom: 12, padding: 14, background: C.aiPurpleBg, border: `1px solid ${C.aiPurple}25` }}>
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.7 }}>{selectedPackage.whyItMatters}</div>
              </Card>
              <Card style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.dim, textTransform: "uppercase", letterSpacing: .7, marginBottom: 8 }}>Packaged workflows</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {selectedPackage.workflows.map((item, i) => (
                    <span key={i} style={{ fontSize: 10, padding: "4px 9px", borderRadius: 999, background: C.surface, border: `1px solid ${C.border}`, color: C.muted }}>{item}</span>
                  ))}
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 11, color: C.dim, textTransform: "uppercase", letterSpacing: .7, marginBottom: 8 }}>What lands in the platform</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {selectedPackage.deliverables.map((item, i) => <div key={i} style={{ fontSize: 12, color: C.text }}>• {item}</div>)}
                </div>
              </Card>
            </ScreenFrame>

            <ScreenFrame accent={C.navy} title="Mock SOR integration calls" sub="These payload snippets illustrate how RegionalEdge connects to core systems of record.">
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {relatedCalls.map(call => (
                  <Card key={call.id} style={{ padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      <Bdg label={call.method} color={C.navy} />
                      <span style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .7 }}>{call.system}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 4 }}>{call.title}</div>
                    <div style={{ fontSize: 11, color: C.navy, marginBottom: 10, wordBreak: "break-all" }}>{call.endpoint}</div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>{call.purpose}</div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                      <CodeBlock label="Request" text={JSON.stringify(call.request, null, 2)} />
                      <CodeBlock label="Response" text={JSON.stringify(call.response, null, 2)} dark={false} />
                    </div>
                  </Card>
                ))}
              </div>
            </ScreenFrame>
          </div>
        </section>

        <section style={{ borderRadius: 26, background: "linear-gradient(135deg, rgba(123,28,46,.08), rgba(30,58,95,.08))", border: `1px solid ${C.border}`, padding: isMobile ? "20px 16px" : "24px 26px" }}>
          <PH title="Closing Frame — Platform Positioning" sub="A concise articulation of what RegionalEdge changes inside a regional bank." accentColor={C.burgundy} />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 14, marginBottom: 16 }}>
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: C.dim, textTransform: "uppercase", letterSpacing: .7, marginBottom: 8 }}>Not another dashboard</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.text, lineHeight: 1.2, marginBottom: 6 }}>One operating layer</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>RegionalEdge brings intelligence, workflow, and evidence into the same system instead of scattering them across disconnected tools.</div>
            </Card>
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: C.dim, textTransform: "uppercase", letterSpacing: .7, marginBottom: 8 }}>Fits their bank</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.text, lineHeight: 1.2, marginBottom: 6 }}>Configurable by operating model</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>Regional banks can buy the core plus only the specialty modules that match their footprint, product mix, and growth plan.</div>
            </Card>
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, color: C.dim, textTransform: "uppercase", letterSpacing: .7, marginBottom: 8 }}>Feels credible</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.text, lineHeight: 1.2, marginBottom: 6 }}>Built for regulated work</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>Every section reinforces that this platform helps them manage growth, risk, compliance, and board communication at the same time.</div>
            </Card>
          </div>

          <AiBox text={`RegionalEdge helps regional banks move faster because executives, bankers, risk, and compliance work from the same intelligence layer. The platform is modular, so it can be configured around how ${mode.label.toLowerCase()} institutions actually operate.`} c={C.burgundy} source="RegionalEdge positioning" />
        </section>
      </div>
    </div>
  );
}
